import { Box, Text, useToast } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import CustomButton from 'components/CustomButton';
import LateChargesFields from 'components/LateChargesFields';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MdAttachMoney } from 'react-icons/md';
import { accountFeatures, updateBilletLateCharges } from 'services/api.service';
import {
  accountLateChargesBody,
  addLateChargesIssues,
  lateChargesFormFrom,
  lateChargesPlaceholders,
  lateChargesSummary,
  lateFieldsOf,
  lateFormOf,
} from 'utils/lateCharges';
import { apiErrorMessage } from 'views/access/carnets/carnet';
import { z } from 'zod';
import SectionCard from './SectionCard';

const schema = z
  .object({
    latePenaltyMode: z.enum(['PERCENT', 'AMOUNT']).optional(),
    latePenaltyPercent: z.string(),
    latePenaltyAmount: z.number().optional(),
    lateMonthlyInterestPercent: z.string(),
  })
  // Sem teto de valor: o padrão vale para cobranças de qualquer valor, e o
  // servidor limita a multa fixa ao valor de cada cobrança na emissão.
  .superRefine((values, ctx) => addLateChargesIssues(values, ctx));

type Values = z.infer<typeof schema>;

export default function LateChargesCard() {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const { data: features, refetch } = useQuery(['features'], accountFeatures);
  const current = features?.billetLateCharges;

  const { control, handleSubmit, reset } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: lateFieldsOf(lateChargesFormFrom(current)),
  });

  // Reabre com o padrão vigente quando ele chega (ou muda depois de salvar).
  useEffect(() => {
    reset(lateFieldsOf(lateChargesFormFrom(current)));
  }, [current, reset]);

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      // Campo vazio = "deixar como está": não vai no corpo.
      await updateBilletLateCharges(accountLateChargesBody(lateFormOf(values)));
      await refetch();
      toast({
        title: 'Sucesso',
        description: 'Multa e juros atualizados.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Erro',
        description: apiErrorMessage(err, 'Não foi possível salvar multa e juros.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  });

  return (
    <SectionCard
      title="Multa e juros por atraso"
      icon={MdAttachMoney}
    >
      <Text
        fontSize="sm"
        color="gray.600"
        mb={1}
      >
        Cobrados do pagador quando o boleto ou o Pix com vencimento passa da data. Valem para as cobranças criadas daqui
        pra frente. Zero isenta.
      </Text>
      {current && (
        <Text
          fontSize="sm"
          fontWeight="semibold"
          mb={3}
        >
          Vigente: {lateChargesSummary(current)}
        </Text>
      )}
      <LateChargesFields
        control={control}
        nameMode="latePenaltyMode"
        namePenaltyPercent="latePenaltyPercent"
        namePenaltyAmount="latePenaltyAmount"
        nameInterest="lateMonthlyInterestPercent"
        placeholders={lateChargesPlaceholders(current)}
        amountLabel="Multa (R$)"
      />
      <Box mt={4}>
        <CustomButton
          variant="solid"
          isLoading={saving}
          onClick={() => onSubmit()}
        >
          Salvar
        </CustomButton>
      </Box>
    </SectionCard>
  );
}
